import { ConfigManager, createConfigManager } from '@unidev-hub/config';
import { Logger } from '@unidev-hub/logger';
import fs from 'fs';
import yaml from 'yaml';
import path from 'path';
import { ConfigOptions } from '../../types';
import { setDefaultConfig } from './defaults';
import { maskSensitiveData } from '../../utils';

/**
 * Standard secret keys to mask in logs
 */
const DEFAULT_SECRET_KEYS = [
  'database.password',
  'database.url',
  'auth.jwt.secret',
  'auth.azure.clientSecret',
  'apiKeys'
];

/**
 * Initialize and load application configuration
 *
 * @param logger Logger instance
 * @param options Configuration options
 * @returns Initialized ConfigManager
 */
export async function initializeConfig(
  logger: Logger,
  options: ConfigOptions = {}
): Promise<ConfigManager> {
  try {
    // Get environment, but don't modify NODE_ENV directly
    const runtimeEnv = process.env.NODE_ENV || 'development';
    const environment = options.environment || runtimeEnv;
    const projectRoot = process.cwd();

    // Log if environment differs from NODE_ENV
    if (environment !== runtimeEnv) {
      logger.info(`Using environment '${environment}' which differs from NODE_ENV '${runtimeEnv}'`);
      // We don't modify process.env.NODE_ENV to avoid the build warning
    }

    // Determine config file path with fallbacks
    const configFilePaths = [
      // Explicit path if provided
      options.path,
      // Environment variable if set
      process.env.CONFIG_PATH,
      // Environment-specific config file
      path.join(projectRoot, 'config', `config.${environment}.yaml`),
      path.join(projectRoot, 'config', `config.${environment}.yml`),
      // Default config file
      path.join(projectRoot, 'config', 'config.yaml'),
      path.join(projectRoot, 'config', 'config.yml'),
      // Root directory config
      path.join(projectRoot, `config.${environment}.yaml`),
      path.join(projectRoot, `config.${environment}.yml`),
      path.join(projectRoot, 'config.yaml'),
      path.join(projectRoot, 'config.yml')
    ].filter(Boolean) as string[];

    // Find first existing config file
    let configFilePath = null;
    for (const filePath of configFilePaths) {
      if (fs.existsSync(filePath)) {
        configFilePath = filePath;
        break;
      }
    }

    logger.info('Initializing configuration', {
      configFilePath: configFilePath || 'using defaults',
      environment
    });

    // Create config manager with the basic options
    const secretKeys = [...DEFAULT_SECRET_KEYS, ...(options.secretKeys || [])];

    const config = createConfigManager({
      environment,
      logger,
      secretKeys,
    });

    // Set environment
    config.set('environment', environment);

    // Store the actual NODE_ENV for reference
    config.set('nodeEnv', runtimeEnv);

    // Set service name if provided
    if (options.serviceName) {
      config.set('service.name', options.serviceName);
    }

    // Try to load and parse the configuration file
    let loadedFromFile = false;

    if (configFilePath) {
      try {
        const rawConfig = fs.readFileSync(configFilePath, 'utf8');
        const configFileContent = yaml.parse(rawConfig);

        // Add parsed config to the config manager
        if (configFileContent && typeof configFileContent === 'object') {
          Object.entries(configFileContent).forEach(([key, value]) => {
            config.set(key, value);
          });

          logger.info('Configuration loaded from file successfully', {
            path: configFilePath
          });

          loadedFromFile = true;
        } else {
          logger.error('Invalid configuration format in file', {
            path: configFilePath
          });
        }
      } catch (parseError) {
        logger.error('Error loading or parsing config file:', parseError as Error);
      }
    } else {
      logger.warn('No configuration file found, using defaults');
    }

    // Apply default configuration values if needed
    if (!loadedFromFile || options.defaults) {
      setDefaultConfig(config, logger, environment, options.defaults);
    }

    // Process environment variables for configuration
    processEnvironmentVariables(config);

    // Log masked configuration values
    const configInfo: Record<string, any> = {
      environment: config.getEnvironment(),
      nodeEnv: config.get('nodeEnv'),
      server: {
        port: config.get<number>('server.port', 3000),
        host: config.get<string>('server.host', '0.0.0.0')
      }
    };

    // Add database info if configured
    const dbUrl = config.get<string>('database.url', '');
    if (dbUrl) {
      configInfo['database'] = {
        url: maskSensitiveData(dbUrl)
      };
    }

    logger.info('Configuration initialized', configInfo);

    return config;
  } catch (error) {
    logger.error('Failed to initialize configuration', error instanceof Error ? error : new Error('Unknown error'));
    throw error;
  }
}

/**
 * Process environment variables for configuration
 * Replaces ${ENV_VAR} patterns in config values with actual environment variable values
 */
function processEnvironmentVariables(config: ConfigManager): void {
  const processValue = (value: any): any => {
    if (typeof value === 'string') {
      // Replace ${ENV_VAR} with environment variable value
      return value.replace(/\${([^}]+)}/g, (match, envVar) => {
        return process.env[envVar] || match;
      });
    } else if (Array.isArray(value)) {
      return value.map(processValue);
    } else if (value !== null && typeof value === 'object') {
      const processed: Record<string, any> = {};
      for (const [key, val] of Object.entries(value)) {
        processed[key] = processValue(val);
      }
      return processed;
    }
    return value;
  };

  // Get all config entries and process them
  const allConfig = config.getAll();

  // Process each key-value pair
  Object.entries(allConfig).forEach(([key, value]) => {
    const processedValue = processValue(value);

    // Only set if the value changed
    if (value !== processedValue) {
      config.set(key, processedValue);
    }
  });
}

export default initializeConfig;
